// src/components/TrainersManager/TrainersManager.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  fetchTrainers,
  insertTrainer,
  updateTrainer,
  deleteTrainer,
} from "../../../store/trainersSlice";
import { fetchDomains } from "../../../store/domainsSlice";
import { fetchEmployees } from "../../../store/empDataSlice";
import {insertTrainersForIct, fetchIctDataByTrainerId} from "../../../store/ictTrainersByIctIdSlice"
import { fetchICTData } from "../../../store/uiSlice";
import "../../../Styles/Common.css";

function TrainersManager() {
  const dispatch = useDispatch();

  // Redux slices
  const { trainers = [], loading, error } = useSelector((state) => state.trainers);
  const { domains = [] } = useSelector((state) => state.domains);
  const { employees = [] } = useSelector((state) => state.empData);
  const { ictData = [] } = useSelector((state) => state.ui);

  // Manage the popup form
  const [ictSearch, setIctSearch] = useState("");
  const [ictSuggestions, setIctSuggestions] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [assignPopup, setAssignPopup] = useState(false);
  const [selectedTrainerId, setSelectedTrainerId] = useState(null);
  const [selectedIctId, setSelectedIctId] = useState(""); // dropdown or text input

  const [showTrainerIctsPopup, setShowTrainerIctsPopup] = useState(false);
  const [trainerIcts, setTrainerIcts] = useState([]);
  const [selectedTrainerName, setSelectedTrainerName] = useState("");


  // Single field "trainerResume" must never be null. We also keep "trainerResumeFile"
  // if user picks a new file.
  const [trainerData, setTrainerData] = useState({
    id: "",
    empId: "",
    trainerDomain: "",
    trainerResume: "",      // always a string
    trainerResumeFile: null, // File object or null
    trainerTrainingMode: "Both",
    trainerCostOnline: "",
    trainerCostClassRoom: "",
    isFL: "No",
    status: "A",
  });

  // For searching employees (live search)
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);

  // Searching & sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("trainerDomain");

  // Checkboxes for filtering by isFL
  const [showFreelancersOnly, setShowFreelancersOnly] = useState(false);
  const [showInhouseOnly, setShowInhouseOnly] = useState(false);

  // Load data from Redux
  useEffect(() => {
    dispatch(fetchTrainers());
    dispatch(fetchDomains());
    dispatch(fetchEmployees());
  }, [dispatch]);

   useEffect(() => {
       if (error) {
         toast.error(
           typeof error === "string" ? error : JSON.stringify(error),
           { toastId: "trainer-err" }      // optional: keep only one copy
         );
       }
     }, [error]);

   useEffect(() => {
    dispatch(fetchICTData());
  }, [dispatch]);


  // Handler for text inputs
  function handleInputChange(e) {
    const { name, value } = e.target;
    setTrainerData((prev) => ({ ...prev, [name]: value }));
  }

  // Handler for file input
  // We store both: the final path in trainerResume, plus the File in trainerResumeFile.
  function handleFileChange(e) {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // We must store a path like "/static/documents/trainer_resumes/<filename>"
      // so trainerResume is never null in DB
      const path = `/static/documents/trainer_resumes/${file.name}`;
      setTrainerData((prev) => ({
        ...prev,
        trainerResumeFile: file, // actual File object
        trainerResume: path,     // final string path in DB
      }));
    }
  }

  const handleViewTrainerIcts = async (trainerId, trainerName) => {
    try {
      const result = await dispatch(fetchIctDataByTrainerId(trainerId)).unwrap();
      setTrainerIcts(result || []);
      setSelectedTrainerName(trainerName);
      setShowTrainerIctsPopup(true);
    } catch (error) {
      toast.error("Failed to fetch trainer ICTs");
    }
  };


  // Reset form data
  function resetForm() {
    setTrainerData({
      id: "",
      empId: "",
      trainerDomain: "",
      trainerResume: "",      // never null
      trainerResumeFile: null,
      trainerTrainingMode: "Both",
      trainerCostOnline: "",
      trainerCostClassRoom: "",
      isFL: "No",
      status: "A",
    });
    setEmployeeSearch("");
    setEmployeeSuggestions([]);
  }

  // Insert or Update
  async function handleInsertOrUpdate(e) {
    e.preventDefault();

    // For brand-new trainers, trainerResume must not be empty
    if (!editMode && !trainerData.trainerResume) {
      toast.error("Resume file is required for new trainers.");
      return;
    }

    if (editMode && !trainerData.id) {
      toast.error("No valid trainer to update (missing ID).");
      return;
    }

    try {
      // We'll pass the entire trainerData. The slice will create FormData
      // with trainerResumeFile if present, and a non-empty trainerResume string.
      if (editMode) {
        await dispatch(updateTrainer(trainerData)).unwrap();
        toast.success("Trainer updated successfully!");
      } else {
        await dispatch(insertTrainer(trainerData)).unwrap();
        toast.success("Trainer added successfully!");
      }
      setShowPopup(false);
      resetForm();
    } catch (err) {
      console.error("Failed to save trainer:", err);
      toast.error("Failed to save trainer.");
    }
  }

  // Toggle status
  async function handleStatusToggle(trainer) {
    const updatedTrainer = {
      ...trainer,
      status: trainer.status === "A" ? "D" : "A",
    };
    try {
      await dispatch(updateTrainer(updatedTrainer)).unwrap();
      toast.success(
        `Trainer ${updatedTrainer.status === "A" ? "Activated" : "Deactivated"}`
      );
    } catch {
      toast.error("Failed to update status");
    }
  }

  // Edit
  function handleEdit(trainer) {
    // Copy the existing data
    setTrainerData({
      id: trainer.id,
      empId: trainer.empId,
      trainerDomain: trainer.trainerDomain,
      trainerResume: trainer.trainerResume, // never null
      trainerResumeFile: null,
      trainerTrainingMode: trainer.trainerTrainingMode,
      trainerCostOnline: trainer.trainerCostOnline,
      trainerCostClassRoom: trainer.trainerCostClassRoom,
      isFL: trainer.isFL,
      status: trainer.status || "A",
    });

    // Fill employee search
    const emp = employees.find((e) => e.id === trainer.empId);
    setEmployeeSearch(emp ? `${emp.empFName} ${emp.empLName}` : "");
    setEmployeeSuggestions([]);
    setEditMode(true);
    setShowPopup(true);
  }

  // Delete
  async function handleDelete(id) {
    if (window.confirm("Are you sure you want to delete this trainer?")) {
      try {
        await dispatch(deleteTrainer(id)).unwrap();
        toast.success("Trainer deleted successfully!");
      } catch {
        toast.error("Failed to delete trainer.");
      }
    }
  }

  // Live search: employees
  function handleEmployeeSearchChange(e) {
    const value = e.target.value;
    setEmployeeSearch(value);
    if (!value.trim()) {
      setEmployeeSuggestions([]);
    } else {
      const suggestions = employees.filter((emp) =>
        (`${emp.empFName} ${emp.empLName}`).toLowerCase().includes(value.toLowerCase())
      );
      setEmployeeSuggestions(suggestions);
    }
  }

  function handleSelectEmployee(emp) {
    setTrainerData((prev) => ({ ...prev, empId: emp.id }));
    setEmployeeSearch(`${emp.empFName} ${emp.empLName}`);
    setEmployeeSuggestions([]);
  }

  // Filtering
  let filteredTrainers = [...trainers];

  // Checkboxes for isFL
  if (showFreelancersOnly && !showInhouseOnly) {
    filteredTrainers = filteredTrainers.filter((t) => t.isFL === "Yes");
  } else if (!showFreelancersOnly && showInhouseOnly) {
    filteredTrainers = filteredTrainers.filter((t) => t.isFL === "No");
  }

  // Search by trainer name
  filteredTrainers = filteredTrainers.filter((t) => {
    const emp = employees.find((e) => e.id === t.empId);
    const trainerName = emp ? `${emp.empFName} ${emp.empLName}` : "";
    return trainerName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Sort
  filteredTrainers.sort((a, b) => {
    if (sortBy === "trainerDomain") {
      return String(a.trainerDomain).localeCompare(String(b.trainerDomain));
    } else if (sortBy === "trainerTrainingMode") {
      return String(a.trainerTrainingMode).localeCompare(String(b.trainerTrainingMode));
    } else if (sortBy === "isFL") {
      return String(a.isFL).localeCompare(String(b.isFL));
    }
    return 0;
  });

  return (
    <div className="trainer-container">
      <ToastContainer position="bottom-right" autoClose={3000} />

      <div className="header-container">
        <h2>Trainer Management</h2>
        <sub>The Cost Range is being denoted as Min(Online) Max(Classroom) </sub>
        <button
          className="add-lead-btn"
          onClick={() => {
            setShowPopup(true);
            setEditMode(false);
            resetForm();
          }}
        >
          <i className="fa fa-plus"></i>
        </button>
      </div>

      {/* Filtering */}
      <div className="feedback-filters">
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <label>
            <input
              type="checkbox"
              checked={showFreelancersOnly}
              onChange={() => setShowFreelancersOnly((prev) => !prev)}
            />
            Show Freelancers
          </label>
          <label>
            <input
              type="checkbox"
              checked={showInhouseOnly}
              onChange={() => setShowInhouseOnly((prev) => !prev)}
            />
            Show In-House
          </label>
        </div>

        <input
          type="text"
          placeholder="Search by Trainer Name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
          style={{ marginLeft: "1rem" }}
        />

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="filter-input"
        >
          <option value="trainerDomain">Sort by Domain</option>
          <option value="trainerTrainingMode">Sort by Training Mode</option>
          <option value="isFL">Sort by Freelancer</option>
        </select>
      </div>

      <table className="table trainer-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Trainer Name</th>
            <th>Domain</th>
            <th>Resume</th>
            <th>Mode</th>
            <th>Cost (Min)</th>
            <th>Cost (Max)</th>
            <th>FL</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTrainers.map((trainer, index) => {
            const emp = employees.find((e) => e.id === trainer.empId);
            const trainerName = emp ? `${emp.empFName} ${emp.empLName}` : "Unknown";
            const domain =
              domains.find((d) => d.id === trainer.trainerDomain)?.domain || "Unknown";

            return (
              <tr key={trainer.id}>
                <td>{index + 1}</td>
                <td
                  style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
                  onClick={() => handleViewTrainerIcts(trainer.id, trainerName)}
                >
                  {trainerName}
                </td>

                <td>{domain}</td>
                <td>
                 {typeof trainer.trainerResume === "string" && trainer.trainerResume ? (
                   <a
                     href={`https://iteyonikservicesinstitute.com/download/documents/trainer_resumes/${trainer.trainerResume.split("/").pop()}`}
                     download
                     title="Download Resume"
                   >
                     <i className="fa fa-file" />
                   </a>
                 ) : (
                   "N/A"
                 )}
               </td>
                <td>{trainer.trainerTrainingMode}</td>
                <td>{trainer.trainerCostOnline}</td>
                <td>{trainer.trainerCostClassRoom}</td>
                <td>{trainer.isFL}</td>
                <td>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={trainer.status === "A"}
                      onChange={() => handleStatusToggle(trainer)}
                    />
                    <span className="slider round"></span>
                  </label>
                </td>
                <td>
                  {trainer.created ? new Date(trainer.created).toLocaleString() : "-"}
                </td>
                <td>
                  <button
                    className="fa fa-plus"
                    title="Assign to ICT"
                    onClick={() => {
                      setSelectedTrainerId(trainer.id);
                      setAssignPopup(true);
                    }}
                  ></button>
                  <button
                    className="icon-btn update-btn fa fa-edit"
                    onClick={() => handleEdit(trainer)}
                  ></button>
                  <button
                    className="icon-btn delete-btn fa fa-trash"
                    onClick={() => handleDelete(trainer.id)}
                  ></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {assignPopup && (
      <div className="popup-overlay">
        <div className="popup-content">
          <h3>Assign Trainer to ICT</h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await dispatch(
                    insertTrainersForIct({
                      ict_id: Number(selectedIctId),
                      trainer_id: selectedTrainerId,
                    })
                  ).unwrap();

                  toast.success("Trainer assigned to ICT successfully!");
                  setAssignPopup(false);
                  setSelectedTrainerId(null);
                  setSelectedIctId("");
                } catch (err) {
                  toast.error("Failed to assign trainer to ICT.");
                  console.error(err);
                }
              }}
            >
            <div className="form-group">
              <label>ICT ID</label>
              <input
                type="text"
                value={ictSearch}
                onChange={(e) => {
                  const value = e.target.value;
                  setIctSearch(value);
                  if (!value.trim()) {
                    setIctSuggestions([]);
                  } else {
                    const matches = ictData.filter((ict) =>
                      ict.ictName.toLowerCase().includes(value.toLowerCase())
                    );
                    setIctSuggestions(matches);
                  }
                }}
                placeholder="Search ICT by Name"
                required
              />
              {ictSuggestions.length > 0 && (
                <div className="suggestions-dropdown">
                  {ictSuggestions.map((ict) => (
                    <div
                      key={ict.id}
                      className="suggestion-item"
                      onClick={() => {
                        setSelectedIctId(ict.id); // Set actual ID
                        setIctSearch(ict.ictName); // Set visible name
                        setIctSuggestions([]);
                      }}
                    >
                      {ict.ictName} (ID: {ict.id})
                    </div>
                  ))}
                </div>
              )}

            </div>
            <div className="form-actions">
              <button type="submit"><i className="fa fa-check"></i></button>
              <button type="button" onClick={() => setAssignPopup(false)}>
                <i className="fa fa-close"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
    )}


      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? "Edit Trainer" : "Add Trainer"}</h3>
            <form onSubmit={handleInsertOrUpdate}>
              {/* Employee search */}
              <div className="form-group">
                <label>Employee</label>
                <input
                  type="text"
                  placeholder="Search Employee by name"
                  value={employeeSearch}
                  onChange={handleEmployeeSearchChange}
                  required
                />
                {employeeSuggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {employeeSuggestions.map((emp) => (
                      <div
                        key={emp.id}
                        className="suggestion-item"
                        onClick={() => handleSelectEmployee(emp)}
                      >
                        {emp.empFName} {emp.empLName}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Domain</label>
                <select
                  name="trainerDomain"
                  value={trainerData.trainerDomain}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Domain</option>
                  {domains.map((dom) => (
                    <option key={dom.id} value={dom.id}>
                      {dom.domain}
                    </option>
                  ))}
                </select>
              </div>

              {/* We store the file path in trainerResume, and the actual File in trainerResumeFile */}
              <div className="form-group">
                <label>Upload Resume (not null)</label>
                <input
                  type="file"
                  name="trainerResumeFile"
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                  // If new trainer => require a file
                  // If editing => only require if there's no existing path
                  required={!editMode && !trainerData.trainerResume}
                />
                {editMode && trainerData.trainerResume && (
                  <p style={{ fontSize: "0.85em", marginTop: "4px" }}>
                    Current resume:
                    <br />
                     <a
                       href={`http://localhost:8000/download/documents/trainer_resumes/${trainerData.trainerResume.split("/").pop()}`}
                       download
                     >
                       {trainerData.trainerResume.split("/").pop()}
                     </a>
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>Training Mode</label>
                <select
                  name="trainerTrainingMode"
                  value={trainerData.trainerTrainingMode}
                  onChange={handleInputChange}
                  required
                >
                  <option value="online">Online</option>
                  <option value="classRoom">ClassRoom</option>
                  <option value="Both">Both</option>
                </select>
              </div>

              <div className="form-group">
                <label>Cost (Min)</label>
                <input
                  type="number"
                  step="0.01"
                  name="trainerCostOnline"
                  placeholder="Min Cost"
                  value={trainerData.trainerCostOnline}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Cost (Max)</label>
                <input
                  type="number"
                  step="0.01"
                  name="trainerCostClassRoom"
                  placeholder="Max Cost"
                  value={trainerData.trainerCostClassRoom}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Freelancer?</label>
                <select
                  name="isFL"
                  value={trainerData.isFL}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="submit">
                  {editMode ? <i className="fa fa-edit"></i> : <i className="fa fa-plus-square"></i>}
                </button>
                <button type="button" onClick={() => setShowPopup(false)}>
                  <i className="fa fa-close"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* all ict under this trainer */}
      {showTrainerIctsPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>ICTs Assigned to {selectedTrainerName}</h3>
            {trainerIcts.length > 0 ? (
              <table className="table">
                  <tr>
                    <th>ICT Name</th>
                    <th>ICT ID</th>
                    <th>ICT Type</th>
                  </tr>
                {trainerIcts.map((ict) => (
                  <tr key={ict.id}>
                    <td>{ict.ictName}</td>
                    <td>(ID: {ict.id})</td>
                    <td>{ict.ictType}</td>
                  </tr>
                ))}
              </table>
            ) : (
              <p>No ICTs assigned.</p>
            )}
            <div className="form-actions">
              <button onClick={() => setShowTrainerIctsPopup(false)}>
                <i className="fa fa-close"></i> Close
              </button>
            </div>
          </div>
        </div>
      )}


      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}

export default TrainersManager;
